<template>
  <OperationFormLayoutSkeleton
    v-if="loading"
    isConfigurationForm
  />
  <form @submit.prevent="save">
    <OperationFormLayout
      v-if="!loading && currentOrganisation"
      :id="$route.params.operationId"
      :formDisabled="disable"
      :formSaving="saving"
      isConfigurationForm
      operationType="plant"
      @cancel="back"
    >
      <!-- Details section -->
      <OperationFormSection
        :modifiers="[
          'no-margin',
          'full-max-width',
          'responsive',
          'default',
          'border-bottom',
        ]"
        :sectionDescription="$t('operations.details_description')"
        :sectionTitle="$t('operations.details_title')"
      >
        <FormRow
          :errors="errors?.operation?.name"
          :label="$t('operations.operation_name_label_plant')"
          labelFor="operation_name"
          :modifiers="['no-padding']"
        >
          <div class="form-row__input form-row__input--no-peek">
            <div :class="{ input: true, 'input--error': errors?.operation?.name }">
              <input
                id="operation_name"
                v-model="operation.name"
                @input="validateInput('name')"
              >
            </div>
          </div>
        </FormRow>
        <FormRow
          :label="$t('operations.operation_division_label')"
          :modifiers="['no-padding']"
        >
          <div class="form-row__input form-row__input--no-peek">
            <DivisionSelect
              v-model="operation.division"
              :divisions="divisions"
            />
          </div>
        </FormRow>
        <FormRow
          v-if="operation.division && operation.division.isNew"
          :errors="divisionNameErrors"
          :label="$t('operations.operation_new_division_label')"
          labelFor="new_division_name"
          :modifiers="['no-padding']"
        >
          <div class="form-row__input form-row__input--no-peek">
            <div :class="{ input: true, 'input--error': divisionNameErrors }">
              <input
                id="new_division_name"
                v-model="operation.division.name"
              >
            </div>
          </div>
        </FormRow>
        <FormRow
          :label="$t('equipment.type')"
          :modifiers="['no-padding']"
        >
          <div class="form-row__input form-row__input--no-peek">
            <Select
              v-model="operation.plant_type_id"
              :items="types"
              :modifiers="['input', 'base', 'short-dropdown']"
              :placeholder="displayType"
            />
          </div>
        </FormRow>
        <FormRow
          :label="$t('operations.operation_time_zone')"
          :modifiers="['no-padding']"
        >
          <div class="form-row__input form-row__input--no-peek">
            <Select
              v-model="operation.time_zone"
              :items="timeZones"
              :modifiers="['input', 'base', 'short-dropdown']"
              :placeholder="displayTimeZone"
            />
          </div>
        </FormRow>
      </OperationFormSection>

      <!-- Configure elements section -->
      <OperationFormSection
        :modifiers="[
          'no-margin',
          'full-max-width',
          'responsive',
          'default',
          'border-bottom',
        ]"
        :sectionDescription="$t('plants.configuration.plant.bounds_and_regions_description')"
        :sectionTitle="$t('plants.configuration.plant.bounds_and_regions')"
      >
        <FormRow
          :errors="boundsErrors"
          :modifiers="['no-padding']"
        >
          <button
            :aria-label="$t('operations.edit_bounds')"
            :class="{
              'operation-settings__bounds-button operation-settings__bounds-button--max-width': true,
              'operation-settings__bounds-button--error': boundsErrors,
            }"
            type="button"
            @click="boundsEditorOpen = true"
          >
            <MapPreview
              canvasClass="operation-settings__bounds-preview"
              :emptyText="$t('operations.no_bounds')"
              emptyClass="operation-settings__no-bounds"
              :polygon="operation.bounds"
            />
          </button>
        </FormRow>
        <div
          class="form-layout__row--mobile form-layout__row--inline form-layout__row--margin-top"
        >
          <div
            class="form-layout__column form-layout__column--inline-button"
            @click="boundsEditorOpen = true"
          >
            <label
              class="diagram-button button"
              for="edit_bounds"
            >
              <PencilSvg class="button__icon button--margin-right" />
              {{ $t('plants.configuration.plant.edit_bounds') }}
            </label>
          </div>
          <div
            class="form-layout__column form-layout__column--inline-button"
          >
            <router-link
              class="button"
              :to="{ name: 'manageRegions' }"
            >
              <ManageRegionSvg class="button__icon button--margin-right" />
              {{ $t('regions.manage_regions') }}
            </router-link>
          </div>
        </div>
      </OperationFormSection>

      <!-- HME section -->
      <OperationFormSection
        v-if="operation.super_user"
        :modifiers="[
          'no-margin',
          'full-max-width',
          'responsive',
          'default',
          'border-bottom',
          'mobile-padding-bottom'
        ]"
        :sectionDescription="$t('plants.configuration.plant.hme_add_ons_description')"
        :sectionTitle="$t('plants.configuration.plant.hme_add_ons')"
      >
        <div class="hme__section hme__section--light">
          <Checkbox
            id="hme_toggle"
            v-model="operation.heavy_mobile_equipment_enabled"
            :modifiers="['switch']"
          >
            <p class="typo-small-title">
              {{ $t('plants.configuration.plant.hme_add_ons_label') }}
            </p>
          </Checkbox>
        </div>
      </OperationFormSection>
    </OperationFormLayout>
  </form>

  <teleport
    v-if="boundsEditorOpen"
    to="body"
  >
    <BoundsEditor
      v-model="operation.bounds"
      :overlays="operation.map_tile_overlays"
      @close="boundsEditorOpen = false"
    />
  </teleport>
</template>

<script setup>
import { computed, inject, onMounted, reactive, ref, watch } from 'vue';

// Utils
import I18n from 'i18n-js';
import dig from '@/functions/dig';
import useAjax from '@/functions/use_ajax';
import { useRoute, useRouter } from 'vue-router';
import timeZones from '@/functions/time_zones';
import isDesktop from '@/functions/responsive';

// Components
import FormRow from '@/components/form/row.vue';
import DivisionSelect from '@/components/operations/division_select.vue';
import OperationFormLayout from '@/components/operations/form/operation_form_layout.vue';
import OperationFormSection from '@/components/operations/form/operation_form_section.vue';
import OperationFormLayoutSkeleton from '@/components/operations/form/operation_form_layout_skeleton.vue';
import Checkbox from '@/components/form/checkbox.vue';
import Select from '@/components/form/select.vue';
import MapPreview from '@/components/generic/map_preview.vue';
import BoundsEditor from '@/components/operations/bounds_editor.vue';

// Svg
import ManageRegionSvg from '@/images/manage-region.svg';
import PencilSvg from '@/images/pencil-24.svg';

const route = useRoute();
const router = useRouter();
const ajax = useAjax();
const loading = ref(false);
const currentOrganisation = inject('currentOrganisation');
const initialOperation = ref(null);
const operation = reactive(createNewOperation());
const boundsEditorOpen = ref(false);
const errors = ref(null);
const saving = ref(false);
const divisions = ref(null);
const disable = ref(true);
const types = ref([]);

const divisionNameErrors = computed(() =>
  dig(errors.value, 'division', 'division', 'name')
);
const boundsErrors = computed(() =>
  dig(errors.value, 'operation', 'bounds')
);
const displayType = computed(() => {
  return operation.type || I18n.t('equipment.no_type');
});
const displayTimeZone = computed(() => {
  return operation.time_zone
    ? operation.time_zone
    : I18n.t('operations.no_operation_time_zone');
});

function createNewOperation() {
  return {
    name: '',
    bounds: null,
    division: null,
    plant_type_id: '',
    map_tile_overlays: [],
    time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

// All endpoints that this component uses
const ENDPOINTS = {
  divisions: `/organisations/${route.params.organisationId}/divisions`,
  operations: `/organisations/${route.params.organisationId}/operations`,
  operation: `/organisations/${route.params.organisationId}/operations/${route.params.operationId}`,
  types: `/organisations/${route.params.organisationId}/plant_types`,
  get post_or_patch() {
    return route.params.operationId ? this.operation : this.operations;
  }
};

/**
 * Form validation, check if the form was updated,
 * if form is updated enable the save button
 */
watch(() => operation, (newValue) => {
    const formFields = [
      'name',
      'division',
      'division_id',
      'plant_type_id',
      'bounds',
      'time_zone',
      'heavy_mobile_equipment_enabled'
    ];

    const changedFields = formFields.filter((field) => {
      // Check if a division exists
      if (!Object.hasOwn(initialOperation.value, field)) return;
      // Check if a division was set (if it was then a division object will exist)
      if ((typeof newValue[field] === 'object' && newValue[field] !== null) && !initialOperation.value[field])
        return newValue[field]?.id !== initialOperation.value['division_id'];
      // Compare division objects
      if (typeof newValue[field] === 'object')
        return (JSON.stringify(newValue[field]) !== JSON.stringify(initialOperation.value[field]));
      // Compare other string instances
      return newValue[field] !== initialOperation.value[field];
    });

    // Check for new division
    const hasNewDivision = !Object.hasOwn(initialOperation.value, 'division') && Object.hasOwn(newValue, 'division') && newValue['division'] !== null;
    disable.value = changedFields.length <= 0 && !hasNewDivision;
  },
  { deep: true }
);

onMounted(() => {
  loadDivisions();
  loadOperation();
  loadTypes();
});

async function loadDivisions() {
  let { ok, data } = await ajax(ENDPOINTS.divisions);

  if (ok) {
    divisions.value = data.divisions;
  }
}

const loadTypes = async () => {
  let { ok, data } = await ajax(ENDPOINTS.types);

  if (ok) {
    types.value = data?.map((item) => ({ value: item.id, label: item.name }));
    types.value.unshift({ value: '', label: I18n.t('equipment.no_type'), dim: true });
  }
};

async function loadOperation() {
  if (route.params.operationId) {
    loading.value = true;
    let { ok, data } = await ajax(ENDPOINTS.operation);

    if (ok) {
      initialOperation.value = data;
      Object.assign(operation, data);
      loading.value = false;
    }
  } else {
    initialOperation.value = createNewOperation();
  }
}

/**
 * Update hme variable only if super_user
 */
function updateHme() {
  if (operation?.super_user) {
    return { heavy_mobile_equipment_enabled: operation.heavy_mobile_equipment_enabled };
  }
}

function formatDivisionAttributes() {
  if (operation.division?.isNew) {
    return {
      division_id: 'new',
      new_division: operation.division.name,
    };
  } else {
    return {
      division_id: operation.division?.id || null,
    };
  }
}

function formatOperationForPost() {
  return {
    operation: {
      name: operation.name,
      bounds: operation.bounds,
      operation_type: 'plant',
      plant_type_id: operation.plant_type_id,
      time_zone: operation.time_zone,
      ...formatDivisionAttributes(),
      ...updateHme()
    },
  };
}

async function save() {
  errors.value = null;
  saving.value = true;

  let method = route.params.operationId ? 'PATCH' : 'POST';
  let { ok, data } = await ajax(ENDPOINTS.post_or_patch, {
    method,
    body: formatOperationForPost(),
  });

  if (ok) {
    router.replace({
      name: isDesktop() ? 'operation' : 'configuration',
      params: { operationId: data.id }
    });
  } else {
    errors.value = data;
  }

  saving.value = false;
}

function back() {
  router.replace({
    name: isDesktop() ? 'operation' : 'configuration',
    params: { operationId: route.params.operationId },
  });
}

function validateInput(field) {
  if (errors.value) {
    errors.value.operation[field] = null;
  }
}
</script>

<style scoped lang="scss">
@import '@/stylesheets/global';

.diagram-container {
  height: 100%;
  overflow: hidden;
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: map-get($background-color, darkest);
  border-radius: map-get($border-radius, heavier);

  .button {
    position: absolute;
    margin: auto;
  }
}

.hme {
  &__section {
    padding: bu(2);
    border-radius: map-get($border-radius, heavier);

    &--light {
      background-color: map-get($background-color, light);
    }
  }
}

.configuration-notice {
  margin: bu(2) bu(5) 0 bu(5);
}
</style>
